import { getAuthToken } from "@/utils/auth/tokenManager";
import { QueryGroup } from "@/types/queryBuilder";

import api from "../Api";
import { Workflow, WorkflowFolder, WorkflowTag } from "@/types/workflow";

interface WorkflowResponse {
    workflows: Workflow[];
    totalWorkflows: number;
    activeWorkflows: number;
    successRate: number;
    totalExecutions: number;
    workflowFolders: WorkflowFolder[];
    tags: WorkflowTag[];
}

interface WorkflowNode {
    id: string;
    workflowId: string;
    type: string;
    isTrigger: boolean;
    positionX: number;
    positionY: number;
    reactFlowId: string;
    config: any;
    createdAt: string;
}

interface WorkflowEdge {
    id: string;
    workflowId: string;
    reactflowSourceId: string;
    reactflowTargetId: string;
    reactflowSourceHandle: string;
    reactflowTargetHandle: string;
    sourceNodeId: string | null;
    targetNodeId: string | null;
    createdAt: string;
}

export interface WorkflowByIdResponse {
    data: Workflow & {
        nodes: WorkflowNode[];
        edges: WorkflowEdge[];
    };
}

export interface CreateWorkflowRequest {
    triggerType: string;
    triggerPosition: {
        positionX: number;
        positionY: number;
    };
    nodeId: string;
}

interface DeleteWorkflowResponse {
    message: string;
    error: boolean;
}

// Field types that can be used in the workflow rules
type FieldType = 'text' | 'boolean' | 'url' | 'select' | 'multi-select' | 'date' | 'number' | 'currency';

// Entity types that can be associated with fields
type EntityType = 'contact' | 'company' | 'ticket' | 'custom_field' | 'custom_object_field';

// Interface for individual field definitions
export interface WorkflowRuleField {
    entityType: EntityType;
    columnname: string;
    label: string;
    type: FieldType;
    options: string[] | null;
    placeholder: string | null;
    table: string;
}

// Interface for a table containing fields
export interface WorkflowRuleTable {
    name: string;
    fields: WorkflowRuleField[];
    id?: string;
}

// Interface for the complete response
interface WorkflowRuleFieldsResponse {
    status: string;
    message: string;
    data: {
        tables: WorkflowRuleTable[];
    };
}

export interface WorkflowConfigurationResponse {
    status: string;
    message: string;
    data: {
        channels: {
            id: string;
            workflowId: string;
            channelType: 'chat' | 'email';
            widgetId: string | null;
            emailChannelId: string | null;
            createdAt: string;
            deletedAt: string | null;
        }[];
        rules: {
            id: string;
            workflowId: string;
            operator: 'and' | 'or';
            parentGroupId: string | null;
            createdBy: string;
            createdAt: string;
            deletedAt: string | null;
            rules: Array<{
                id: string;
                workflowId: string;
                workflowRuleGroupId: string;
                entityType: string;
                standardFieldName: string | null;
                customFieldId: string | null;
                customObjectId: string | null;
                customObjectFieldId: string | null;
                operator: string;
                value: string;
                sourceFieldType: string | null;
                createdAt: string;
                deletedAt: string | null;
                rules?: any[];
            } | {
                id: string;
                workflowId: string;
                operator: 'and' | 'or';
                parentGroupId: string;
                createdBy: string;
                createdAt: string;
                deletedAt: string | null;
                rules: any[];
            }>;
        };
    };
};

export interface ReusableBranch {
    id: string;
    branchName: string;
}

export interface WorkflowReusableBranchResponse {
    status: string;
    message: string;
    data: ReusableBranch[];
}

export const workflowService = {
    async getAllWorkflows(): Promise<WorkflowResponse> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.get('/workflow', {
                params: { workspace_id: workspaceId },
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error fetching workflows:', error);
            throw error;
        }
    },

    async createNewWorkflowFolder(folder: Partial<WorkflowFolder>): Promise<WorkflowFolder> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.post('/workflow/folder', {
                name: folder.name,
                parentId: folder.parentId,
            }, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error creating workflow folder:', error);
            throw error;
        }
    },

    async deleteWorkflowFolder(folderId: string): Promise<WorkflowFolder> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.delete(`/workflow/folder/${folderId}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error deleting workflow folder:', error);
            throw error;
        }
    },

    async updateWorkflowFolder(folderId: string, folder: Partial<WorkflowFolder>): Promise<WorkflowFolder> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.patch(`/workflow/folder/${folderId}`, folder, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error updating workflow folder:', error);
            throw error;
        }
    },

    async getWorkflowById(workflowId: string): Promise<WorkflowByIdResponse> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.get(`/workflow/${workflowId}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response?.data;
        } catch (error) {
            console.error('Error fetching workflow by ID:', error);
            throw error;
        }
    },

    async createNewWorkflow(workflowDetails: CreateWorkflowRequest): Promise<Workflow> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.post('/workflow', workflowDetails, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error creating new workflow:', error);
            throw error;
        }
    },

    async deleteWorkflow(workflowId: string): Promise<DeleteWorkflowResponse> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.delete(`/workflow/${workflowId}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error deleting workflow:', error);
            throw error;
        }
    },

    async updateWorkflowTags(workflowId: string, tagUuids: string[]): Promise<WorkflowTag[]> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.patch(`/workflow/tags/${workflowId}`, { tags: tagUuids }, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error updating workflow tags:', error);
            throw error;
        }
    },

    async saveWorkflowChanges(workflowId: string, workflow: {
        workflowConfig: {
            name: string
        },
        nodes: WorkflowNode[];
        edges: WorkflowEdge[];
    }): Promise<Workflow> {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const token = getAuthToken();

            if (!workspaceId) {
                throw new Error('Workspace ID not found');
            }

            const response = await api.post(`/workflow/${workflowId}`, workflow, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data?.data;
        } catch (error) {
            console.error('Error saving workflow changes:', error);
            throw error;
        }
    },

    async getWorkflowRuleFields(): Promise<WorkflowRuleFieldsResponse> {
        try {
            const token = getAuthToken();

            const response = await api.get(`/workflow/getWorkflowRuleFields`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            })

            return response.data;
        } catch (error) {
            console.error('Error fetching workflow rule fields:', error);
            throw error;
        }
    },

    async getWorkflowConfiguration(workflowId: string): Promise<WorkflowConfigurationResponse> {
        try {
            const token = getAuthToken();

            const response = await api.get(`/workflow/${workflowId}/workflow-configuration`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching workflow configuration:', error);
            throw error;
        }
    },
    async updateWorkflowConfiguration(workflowId: string, configuration: any): Promise<any> {
        try {
            const token = getAuthToken();

            const response = await api.post(`/workflow/${workflowId}/update-configuration`, configuration, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error updating workflow configuration:', error);
            throw error;
        }
    },
    async getWorkflowReusableBranches(): Promise<WorkflowReusableBranchResponse> {
        try {
            const token = getAuthToken();

            const response = await api.get(`workflow/getWorkflowReusableNodes`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching workflow reusable branches:', error);
            throw error;
        }
    },
    async activateWorkflow(workflowId: string): Promise<Workflow> {
        try {
            const token = getAuthToken();

            const response = await api.post(`/workflow/${workflowId}/activate`, {}, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error activating workflow:', error);
            throw error;
        }
    },
    async disableWorkflow(workflowId: string): Promise<Workflow> {
        try {
            const token = getAuthToken();

            const response = await api.post(`/workflow/${workflowId}/disable`, {}, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error activating workflow:', error);
            throw error;
        }
    },
    async updateWorkflowFolderId(workflowId: string, folderId: string | null): Promise<Workflow> {
        try {
            const response = await api.post(`/workflow/updateFolderForWorkflow`, { workflowId, folderId });

            return response.data;
        } catch (error) {
            console.error('Error updating workflow folder id:', error);
            throw error;
        }
    },
    async duplicateWorkflow(workflowId: string): Promise<Workflow> {
        try {
            const response = await api.post(`/workflow/duplicateWorkflow`, {
                workflowId
            });

            return response.data?.data?.duplicatedWorkflow;
        } catch (error) {
            console.error('Error duplicating workflow:', error);
            throw error;
        }
    }
}
